import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as toastr from 'toastr';

import policyActions from '../../policy/policyActions';
import policyAPI, { defaultTrustee } from '../../policy/policyAPI';

import PolicyBeneficiariesList from '../../policy/PolicyBeneficiariesList';
import PolicyBeneficiaryContainer from '../../policy/PolicyBeneficiaryContainer';
import { Button } from '../../components/buttons';
import { DropdownInput } from '../../components/forms';
import { IconAdd } from '../../components/icons';

import { ensureUserHasRoleAdminSite } from '../../auth/authComponents';
import Utils, { BeneficiaryStatus, UtilsBeneficiaries,
BeneficiaryTypes, SecurityRoles as roles } from '../../common/utils';
import FileUpload from '../../components/forms/FileUpload';

interface Props {
	className?: string;
	currentBeneficiaries: CustomerPolicyBeneficiaryDto[];
	pendingBeneficiaries: CustomerPolicyBeneficiaryDto[];
	policies: CustomerPolicy[];
	deleteSucceed: Boolean;
	actions: any;
	history: any;
	match: any;
}
interface State {
	isLoading: boolean;
	isChangingStatus: Set<string>;
	policiesWithConfirmedBeneficiaries: CustomerPolicy[];
	policiesWithPendingBeneficiaries: CustomerPolicy[];
	showingEdit: boolean;
	isDeleting: boolean;
}
class CustomerBeneficiariesPage extends React.Component<Props, State> {
	state = {
		isLoading: true,
		isChangingStatus: new Set(),
		policiesWithConfirmedBeneficiaries: [] as CustomerPolicy[],
		policiesWithPendingBeneficiaries: [] as CustomerPolicy[],
		showingEdit: false,
		isDeleting: false,
	};

	componentDidMount() {
		this.loadData();
	}
	loadData = () => {
		const p1 = this.props.actions.fetchCustomerPolicies(this.props.match.params.customerId);
		const p2 = this.props.actions.fetchCustomerPolicyBeneficiaries(this.props.match.params.customerId);
		return Promise.all([p1, p2])
			.then(() => {
				const currentPolicyIds = new Set();
				this.props.currentBeneficiaries.forEach(beneficiary => {
					currentPolicyIds.add(beneficiary.customerPolicyId);
				});

				const pendingPolicyIds = new Set();
				this.props.pendingBeneficiaries.forEach(beneficiary => {
					pendingPolicyIds.add(beneficiary.customerPolicyId);
				});

				const policiesWithConfirmedBeneficiaries = this.props.policies.filter(p => currentPolicyIds.has(p.id));
				const policiesWithPendingBeneficiaries = this.props.policies.filter(p => pendingPolicyIds.has(p.id));

				this.setState({ 
					policiesWithConfirmedBeneficiaries, 
					policiesWithPendingBeneficiaries, 
					isLoading: false 
				});
			});
	}
	checkIfNeededAndReloadData = () => {
		// if we just came back from a beneficiary edit, reload the data in case something changed.		
		if (!this.props.match.params.pageId && this.state.showingEdit) {
			this.setState({showingEdit: false});
			this.loadData();
		}
	}

	onEdit = (row: CustomerPolicyBeneficiaryDto) => {
		this.setState({showingEdit: true});
		this.props.history.push(`/admin/customers/${this.props.match.params.customerId}/beneficiaries/${row.id}`);
	}

	onDelete = (row: CustomerPolicyBeneficiaryDto) => {
		if (this.state.isDeleting === true) {
			return;
		}
		this.setState({isDeleting: true});
		this.props.actions.deleteCustomerPolicyBeneficiary(row.id, row.statusId)
		.then(() => {
			if (this.props.deleteSucceed) {
				toastr.success('Beneficiary deleted!');
			} else {
				toastr.error('Each policy requires at least one beneficiary');
			}
			this.props.actions.fetchCustomerPolicyBeneficiaries(this.props.match.params.customerId);
		})
		.then(() => {
			this.setState({isDeleting: false});
			this.loadData();
		});
			
	}
	onPolicyChange = (policy: CustomerPolicy, e: any) => {

		policy.beneficiariesStatus = e.target.value;

		this.props.actions.setCustomerPolicies(policy);
	}
	savePolicyStatus = (policy: CustomerPolicy) => {
		this.setIsChangingStatus(policy.id!.toString(), false);

		this.props.actions.saveAdminCustomerPolicy(policy)
			.then(() => {
				toastr.success('Policy Status Saved!');
				this.loadData();
			});
	}
	setIsChangingStatus = (policyId: string, isChanging: boolean) => {
		const isChangingStatus = new Set(this.state.isChangingStatus);
		if (isChanging) {
			isChangingStatus.add(policyId);
		} else {
			isChangingStatus.delete(policyId);
		}

		this.setState({ isChangingStatus });
	}

	onManageDisbursement = (beneficiaries: CustomerPolicyBeneficiaryDto[], policy: CustomerPolicy, isCurrent: Boolean) => {
		this.props.actions.setCustomerPolicy(policy);
		let filteredBeneficiaries: CustomerPolicyBeneficiaryDto[] = [];
		if (!isCurrent) {
			filteredBeneficiaries = this.props.pendingBeneficiaries
																	.filter(b => b.customerPolicyId === policy.id);
			this.props.actions.setCustomerPolicyBeneficiaries(filteredBeneficiaries);
			
		} else {
			filteredBeneficiaries = this.props.currentBeneficiaries
																	.filter(b => b.customerPolicyId === policy.id);
			this.props.actions.setCustomerPolicyBeneficiaries(filteredBeneficiaries);
		}
		if (filteredBeneficiaries.some(b => b.isUnderage === true)) {
			const trustee = filteredBeneficiaries.find(b => b.typeId === BeneficiaryTypes.Trustee);
			if (trustee) {
				this.props.actions.setTrustee(trustee);
			} else {
				let newTrustee = defaultTrustee;
				this.props.actions.setTrustee(newTrustee);
			}
		}
		this.props.history.push(`/admin/customers/${this.props.match.params.customerId}/beneficiaries-disbursement`);
	}

	validatePercentage = (policy: CustomerPolicy) => {
		const policyBeneficiaries = this.props.pendingBeneficiaries.filter(b => b.customerPolicyId === policy.id);
		return UtilsBeneficiaries.isDisbursementValid(policyBeneficiaries);
	}

	onUpload = (file: any, customerId: string, policy: CustomerPolicy) => {
		const { sendBeneficiaryNotification } = this.props.actions;
		policyAPI.uploadSignedBeneficiaryDocumentUrl(customerId, policy.id!, file)
		.then(() => {
			const updatedPolicy = {...policy, beneficiariesStatus: BeneficiaryStatus.Confirmed};
			return this.props.actions.saveAdminCustomerPolicy(updatedPolicy);
		})
		.then(() => this.props.actions.sendBeneficiaryUploadedConfirmation(customerId, policy.id))
		.then(() => {
			const beneficiaries = this.props.pendingBeneficiaries.filter(pb => pb.customerPolicyId === policy.id);
			beneficiaries.reduce(
				function (promise: any, beneficiary: any) {
					if (beneficiary.shouldSendNotification) {
						return promise.then(function () {
							return sendBeneficiaryNotification(customerId, beneficiary.id);
						});
					}
				},
				Promise.resolve()
			);
		})
		.then(() => this.loadData())
		.then(() => toastr.info('Document uploaded and policy beneficiaries marked confirmed.'))
		.catch(() => {
			toastr.error('There was an issue confirming and uploading the beneficiaries.  Please contact support');
		});
	}
	onDownloadFileClicked = (customerId: string, policyId: string) => {
		policyAPI.fetchSignedBeneficiaryDocumentUrl(customerId, policyId)
		.then((url) => {
			window.open(url);
		});
	}

	renderList = () => {
		return (
			<div className={this.props.className}>
				{this.renderCurrentPoliciesList()}
				{this.renderPendingPoliciesList()}
				<div className="workflow-progress-actions mt-4">
					<Button
						to={`/admin/customers/${this.props.match.params.customerId}/beneficiaries/new`}
						className="btn-outline-primary workflow-progress-action"
					>
						<span className="align-middle mx-2">Add a beneficiary</span>
						<IconAdd />
					</Button>
				</div>
			</div>
		);
	}
	renderCurrentPoliciesList = () => {
		return (
			<div className="mb-4">
				<h3>Current</h3>
				{this.renderPoliciesList(this.state.policiesWithConfirmedBeneficiaries, this.props.currentBeneficiaries, true)}
			</div>
		);
	}
	renderPendingPoliciesList = () => {
		return (
			<div>
				<h3>Pending</h3>
				{this.renderPoliciesList(this.state.policiesWithPendingBeneficiaries, this.props.pendingBeneficiaries)}
			</div>
		);
	}
	renderPoliciesList = (
		policies: CustomerPolicy[],
		beneficiaries: CustomerPolicyBeneficiaryDto[],
		isCurrent: boolean = false) => {
		return (
			<ul className="list-unstyled mb-0 customer-dashboard-plans">
				{policies.length > 0 ? (
					policies.map(policy => {
						// disable editing on current policy beneficiaries that have pending changes
						const readOnly = isCurrent 
							&& this.props.pendingBeneficiaries.filter(b => b.customerPolicyId === policy.id).length > 0;

						return (<li key={policy.id} className="customer-dashboard-plan">
							<div className="customer-dashboard-plan-info">
								<span className="d-inline large">

									<strong>Policy {policy.number}&nbsp;</strong>
									{this.state.isChangingStatus.has(policy.id) && !isCurrent
										?
										<span>
											<DropdownInput 
												name="beneficiariesStatus" 
												defaultOption={'-'} 
												options={[
													{
														text: 'Pending paperwork',
														value: BeneficiaryStatus.PendingPaperwork
													},
													{
														text: 'Pending receipt',
														value: BeneficiaryStatus.PendingReceipt
													},
													{
														text: 'Confirmed',
														value: BeneficiaryStatus.Confirmed
													}
												]}
												onChange={(e: any) => this.onPolicyChange(policy, e)} 
												className="d-inline-block" 
												value={policy.beneficiariesStatus} 
											/>
											<Button 
												className="btn-link text-secondary small" 
												disabled={!this.validatePercentage(policy)} 
												onClick={() => this.savePolicyStatus(policy)}
											>
												save
											</Button>
											<Button 
												className="btn-link text-secondary small" 
												onClick={() => this.setIsChangingStatus(policy.id!, false)}
											>
												cancel
											</Button>
										</span>
										:
										this.renderStatus(policy, isCurrent)}
								</span>
								<span className="d-inline">
									<Button 
										className="btn-outline-primary"
										disabled={readOnly}
										onClick={() => this.onManageDisbursement(this.props.pendingBeneficiaries, policy, isCurrent)}
									>
										<em className="align-middle mx-2">Manage disbursement</em>
									</Button>
								</span>
							</div>
							{this.renderBeneficiaryList(beneficiaries, policy.id!, readOnly)}
							<div>
								{!isCurrent &&
									<span className="d-inline">
										<FileUpload 
											label="Upload Signed Form & Confirm"
											onDrop={(file: any) => { this.onUpload(file, this.props.match.params.customerId, policy); }} 
											disabled={!this.validatePercentage(policy)} 
										/>
									</span>}
							</div>
						</li>);
					})
				) : (
						<li>
							<em className="reduced-em">No policies</em>
						</li>
					)}
			</ul>
		);
	}
	renderBeneficiaryList = (beneficiaries: CustomerPolicyBeneficiaryDto[], policyId: string, readOnly: boolean) => {
		const filteredBeneficiaries = beneficiaries
		.filter(b => b.customerPolicyId === policyId && b.typeId !== BeneficiaryTypes.Trustee);
		return (
			<ul>
				<PolicyBeneficiariesList
					beneficiaries={filteredBeneficiaries}
					onEdit={this.onEdit}
					onDelete={this.onDelete}
					loading={this.state.isLoading}
					readOnly={readOnly}
				/>
			</ul>
		);
	}
	renderStatus = (policy: CustomerPolicy, isCurrent: Boolean) => {
		return (
			<span>
				{isCurrent ? '' :
					<span>
						{Utils.getPickListItemDescription(policy.beneficiariesStatus)}
						<Button
							className="btn-link text-secondary small"
							onClick={() => this.setIsChangingStatus(policy.id!, true)}
						>
							change
						</Button>
					</span>}
			</span>
		);
	}

	renderForm = () => {
		return (
			<PolicyBeneficiaryContainer
				baseUrl={`/admin/customers/${this.props.match.params.customerId}`}
				history={this.props.history}
				match={this.props.match}
			/>
		);
	}

	render() {
		this.checkIfNeededAndReloadData();
		if (this.props.match.params.pageId) {
			return this.renderForm();
		}
		else {
			return this.renderList();
		}
	}
}
function mapStateToProps(state: any) {
	const currentBeneficiaries = state.policy.beneficiaries.current
	.sort(UtilsBeneficiaries.sortPrimaryThenByName);
	const pendingBeneficiaries = state.policy.beneficiaries.pending
	.sort(UtilsBeneficiaries.sortPrimaryThenByName);
	return {
		currentBeneficiaries,
		pendingBeneficiaries,
		policies: state.policy.policies,
		deleteSucceed: state.policy.deleteSucceed,
	};
}
function mapDispatchToProps(dispatch: any) {
	const actions = { ...policyActions };
	return {
		actions: bindActionCreators(actions, dispatch)
	};
}
const connectedPage = connect(mapStateToProps, mapDispatchToProps)(CustomerBeneficiariesPage);
const adminRoles = [roles.Administrator, roles.Agent];
export default ensureUserHasRoleAdminSite(connectedPage as any, adminRoles);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/admin/customers/CustomerBeneficiariesPage.tsx